import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ShoppingCart, Plus, Coffee, CupSoda, Cookie, Salad, Flame, ArrowRight, Check, 
  Users, Clock, Award, Snowflake, Building2, BarChart3, Bot, Calendar, Truck,
  Heart, Leaf, Zap, Shield, Sparkles, ChefHat, Package, CreditCard, User
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import InteractiveDemo from '../components/InteractiveDemo';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categoryIcons = {
  'snowflake': Snowflake,
  'coffee': Coffee,
  'cup-soda': CupSoda,
  'cookie': Cookie,
  'salad': Salad,
  'flame': Flame
};

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('empresa'); // 'empresa' or 'cliente'
  const { addItem } = useCart();
  const { user, login } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        await fetch(`${API}/seed`, { method: 'POST' });
        
        const [catRes, prodRes] = await Promise.all([
          fetch(`${API}/categories`),
          fetch(`${API}/products`)
        ]);
        
        const catData = await catRes.json();
        const prodData = await prodRes.json();
        
        const sortedCategories = [...catData].sort((a, b) => (a.order || 99) - (b.order || 99));
        setCategories(sortedCategories);
        setProducts(prodData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const filteredProducts = selectedCategory
    ? products.filter(p => p.category_id === selectedCategory)
    : products;

  const handleAddToCart = (product) => {
    addItem(product);
    toast.success(`${product.name} agregado al carrito`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-emerald-700 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // Content for Empresa view
  const EmpresaContent = () => (
    <div className="py-16 px-4 md:px-8 lg:px-12 bg-emerald-900 relative overflow-hidden">
      {/* Subtle grain texture */}
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")' }} />
      
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-4">
            Gestiona la alimentación de tu equipo con inteligencia artificial
          </h2>
          <p className="text-emerald-200 text-lg max-w-2xl mx-auto">
            Automatiza pedidos, reduce costos y mejora el bienestar de tus empleados.
          </p>
        </div>

        {/* Bento Grid - Features */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {/* AI Agents */}
          <div className="lg:col-span-2 bg-emerald-800/40 backdrop-blur-sm rounded-2xl p-8 border border-emerald-700/50 hover:border-emerald-600 transition-all">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 bg-emerald-700/50 rounded-xl flex items-center justify-center">
                <Bot className="w-6 h-6 text-emerald-300" />
              </div>
              <Badge className="bg-orange-500 text-white border-0 text-xs">
                AUTOMATIZADO
              </Badge>
            </div>
            <h3 className="text-xl font-bold text-white mb-3">
              8 Agentes de IA para tu operación
            </h3>
            <p className="text-emerald-200 mb-6 text-sm">
              Forecasts, P&L, programación de menús, compras, rutas y más.
            </p>
            <div className="grid grid-cols-2 gap-3">
              {['Forecast Ventas', 'Análisis P&L', 'Plan Producción', 'Rutas Entrega'].map((item) => (
                <div key={item} className="flex items-center gap-2 text-emerald-300 text-sm">
                  <Check className="w-4 h-4" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Subscriptions */}
          <div className="bg-emerald-800/40 backdrop-blur-sm rounded-2xl p-8 border border-emerald-700/50 hover:border-emerald-600 transition-all">
            <CreditCard className="w-10 h-10 text-emerald-400 mb-4" />
            <h4 className="text-lg font-bold text-white mb-2">Suscripciones Mensuales</h4>
            <p className="text-emerald-300 text-sm">
              Planes flexibles. Facturación automática y reportes de consumo.
            </p>
          </div>

          {/* KDS */}
          <div className="bg-emerald-800/40 backdrop-blur-sm rounded-2xl p-8 border border-emerald-700/50 hover:border-emerald-600 transition-all">
            <ChefHat className="w-10 h-10 text-emerald-400 mb-4" />
            <h4 className="text-lg font-bold text-white mb-2">Kitchen Display System</h4>
            <p className="text-emerald-300 text-sm">
              Producción en tiempo real. Sincroniza cocina y entregas.
            </p>
          </div>

          {/* Analytics */}
          <div className="bg-emerald-800/40 backdrop-blur-sm rounded-2xl p-8 border border-emerald-700/50 hover:border-emerald-600 transition-all">
            <BarChart3 className="w-10 h-10 text-emerald-400 mb-4" />
            <h4 className="text-lg font-bold text-white mb-2">Analytics Avanzados</h4>
            <p className="text-emerald-300 text-sm">
              Dashboards con métricas de ventas, clientes y operaciones.
            </p>
          </div>

          {/* Logistics */}
          <div className="bg-emerald-800/40 backdrop-blur-sm rounded-2xl p-8 border border-emerald-700/50 hover:border-emerald-600 transition-all">
            <Truck className="w-10 h-10 text-emerald-400 mb-4" />
            <h4 className="text-lg font-bold text-white mb-2">Logística Optimizada</h4>
            <p className="text-emerald-300 text-sm">
              Rutas inteligentes y tracking en tiempo real.
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link to="/login">
            <Button className="bg-white text-emerald-900 hover:bg-stone-100 rounded-full px-10 py-6 text-lg font-semibold shadow-xl transition-all hover:-translate-y-1" data-testid="empresa-cta">
              Solicitar Demo Empresarial
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );

  // Content for Cliente view
  const ClienteContent = () => (
    <div className="py-16 px-4 md:px-8 lg:px-12 bg-amber-50/50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-stone-900 tracking-tight mb-4">
            Disfruta comida saludable sin complicaciones
          </h2>
          <p className="text-stone-600 text-lg max-w-2xl mx-auto">
            92 productos frescos en 10 categorías. Pide desde tu escritorio y recibe directo en tu oficina.
          </p>
        </div>

        {/* Asymmetric Layout */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: Image with Stats */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1662714208483-3480ccd2de39?w=800"
                alt="Comida saludable"
                className="w-full aspect-[4/3] object-cover"
              />
              {/* Floating Stats Card */}
              <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg">
                <div className="flex items-center justify-between text-center">
                  <div>
                    <p className="text-2xl font-bold text-emerald-700">92</p>
                    <p className="text-xs text-stone-500">Productos</p>
                  </div>
                  <div className="h-10 w-px bg-stone-200" />
                  <div>
                    <p className="text-2xl font-bold text-emerald-700">10</p>
                    <p className="text-xs text-stone-500">Categorías</p>
                  </div>
                  <div className="h-10 w-px bg-stone-200" />
                  <div>
                    <p className="text-2xl font-bold text-emerald-700">100%</p>
                    <p className="text-xs text-stone-500">Saludable</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Benefits */}
          <div className="space-y-4">
            {[
              {
                icon: Leaf,
                title: 'Comida Fresca y Saludable',
                description: 'Ingredientes de calidad, preparados diariamente.',
                color: 'bg-green-50 text-green-700'
              },
              {
                icon: Zap,
                title: 'Pedidos Rápidos',
                description: 'Ordena en segundos. Recibe en tu escritorio.',
                color: 'bg-yellow-50 text-yellow-700'
              },
              {
                icon: Shield,
                title: 'Pagos Seguros',
                description: 'Stripe, PayPal o cargo a tu empresa.',
                color: 'bg-blue-50 text-blue-700'
              },
              {
                icon: Sparkles,
                title: 'Nutricionista IA',
                description: 'Elige según tus objetivos de salud.',
                color: 'bg-purple-50 text-purple-700'
              }
            ].map((benefit, i) => (
              <div 
                key={benefit.title}
                className="flex gap-4 p-5 bg-white rounded-xl border border-stone-100 hover:border-emerald-200 hover:shadow-md transition-all"
              >
                <div className={`w-12 h-12 ${benefit.color} rounded-xl flex items-center justify-center shrink-0`}>
                  <benefit.icon className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="font-semibold text-stone-900">{benefit.title}</h4>
                  <p className="text-stone-500 text-sm">{benefit.description}</p>
                </div>
              </div>
            ))}

            {/* CTA */}
            <div className="pt-4">
              <Link to="/menu">
                <Button className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-10 py-6 text-lg font-semibold shadow-lg shadow-orange-500/20 transition-all hover:-translate-y-1 w-full sm:w-auto" data-testid="cliente-cta">
                  Explorar Menú
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      
      {/* Hero Section - Compact */}
      <section className="pt-24 pb-12 px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto text-center">
          <Badge className="bg-orange-100 text-orange-700 border-0 mb-6 px-4 py-2 text-sm font-medium tracking-wide">
            FOOD AS A SERVICE
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-stone-900 tracking-tight leading-tight mb-6">
            Alimentación <span className="text-emerald-700">Inteligente</span> para tu Oficina
          </h1>
          <p className="text-lg text-stone-600 mb-10 max-w-2xl mx-auto">
            La plataforma que conecta empresas con comida saludable. Automatiza pedidos y mejora el bienestar de tu equipo.
          </p>
        </div>
      </section>

      {/* Toggle Buttons - SOY EMPRESA / SOY CLIENTE */}
      <section className="px-4 md:px-8 lg:px-12 pb-8">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg p-2 flex">
            <button
              onClick={() => setViewMode('empresa')}
              className={`flex-1 flex items-center justify-center gap-3 py-5 rounded-xl font-semibold text-lg transition-all ${
                viewMode === 'empresa'
                  ? 'bg-emerald-700 text-white shadow-lg'
                  : 'text-stone-600 hover:bg-stone-50'
              }`}
              data-testid="toggle-empresa"
            >
              <Building2 className="w-6 h-6" />
              Soy Empresa
            </button>
            <button
              onClick={() => setViewMode('cliente')}
              className={`flex-1 flex items-center justify-center gap-3 py-5 rounded-xl font-semibold text-lg transition-all ${
                viewMode === 'cliente'
                  ? 'bg-orange-500 text-white shadow-lg'
                  : 'text-stone-600 hover:bg-stone-50'
              }`}
              data-testid="toggle-cliente"
            >
              <User className="w-6 h-6" />
              Soy Cliente
            </button>
          </div>
        </div>
      </section>

      {/* Dynamic Content Based on Toggle */}
      <section className="transition-all duration-300">
        {viewMode === 'empresa' ? <EmpresaContent /> : <ClienteContent />}
      </section>

      {/* Stats */}
      <section className="py-16 px-4 md:px-8 lg:px-12 bg-white border-y border-stone-200">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { icon: Building2, value: '500+', label: 'Empresas activas' },
              { icon: Package, value: '92', label: 'Productos frescos' },
              { icon: Clock, value: '30 min', label: 'Ahorro diario' },
              { icon: Heart, value: '100%', label: 'Comida saludable' }
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <stat.icon className="w-7 h-7 text-emerald-700" />
                </div>
                <p className="text-3xl font-bold text-stone-900 mb-1">{stat.value}</p>
                <p className="text-stone-500 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Preview */}
      <section className="py-16 px-4 md:px-8 lg:px-12 bg-stone-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-stone-900 mb-3">Explora Nuestro Menú</h2>
            <p className="text-stone-600">10 categorías con productos frescos</p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-5 py-2.5 rounded-full font-medium text-sm transition-all ${
                !selectedCategory
                  ? 'bg-emerald-700 text-white shadow-lg shadow-emerald-700/20'
                  : 'bg-white text-stone-700 hover:bg-stone-100 border border-stone-200'
              }`}
              data-testid="category-all"
            >
              Todos
            </button>
            {categories.slice(0, 5).map(cat => {
              const IconComponent = categoryIcons[cat.icon] || Coffee;
              return (
                <button
                  key={cat.category_id}
                  onClick={() => setSelectedCategory(cat.category_id)}
                  className={`px-5 py-2.5 rounded-full font-medium text-sm transition-all flex items-center gap-2 ${
                    selectedCategory === cat.category_id
                      ? 'bg-emerald-700 text-white shadow-lg shadow-emerald-700/20'
                      : 'bg-white text-stone-700 hover:bg-stone-100 border border-stone-200'
                  }`}
                  data-testid={`category-${cat.category_id}`}
                >
                  <IconComponent className="w-4 h-4" />
                  {cat.name_es}
                </button>
              );
            })}
          </div>

          {/* Products Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {filteredProducts.slice(0, 4).map((product) => (
              <div
                key={product.product_id}
                className="group bg-white rounded-xl border border-stone-100 overflow-hidden hover:border-emerald-200 hover:shadow-lg transition-all"
                data-testid={`product-card-${product.product_id}`}
              >
                <div className="aspect-square overflow-hidden bg-stone-100">
                  <img
                    src={product.image_url || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400'}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-stone-900 group-hover:text-emerald-700 transition-colors text-sm mb-1">
                    {product.name}
                  </h3>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold text-orange-500">
                      ${product.price.toFixed(0)}
                    </span>
                    <Button
                      size="sm"
                      className="bg-emerald-700 hover:bg-emerald-800 rounded-full px-3 h-8 text-xs"
                      onClick={() => handleAddToCart(product)}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/menu">
              <Button variant="outline" className="rounded-full px-8 py-5 border-stone-300 hover:bg-stone-100" data-testid="ver-menu-completo-btn">
                Ver los 92 Productos
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 px-4 md:px-8 lg:px-12 bg-gradient-to-br from-emerald-700 to-emerald-800">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            ¿Listo para transformar tu pausa de comida?
          </h2>
          <p className="text-emerald-100 mb-8">
            Únete a más de 500 empresas que ya disfrutan de alimentación inteligente
          </p>
          <Link to="/login">
            <Button className="bg-white text-emerald-700 hover:bg-stone-100 rounded-full px-10 py-6 text-lg font-semibold shadow-xl" data-testid="final-cta-btn">
              Comenzar Ahora
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-4 md:px-8 lg:px-12 bg-stone-900 text-stone-400">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img
                src="/logo.png"
                alt="Lunch & Roll"
                className="h-10 w-auto mb-3"
              />
              <p className="text-sm">Alimentación inteligente para oficinas.</p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-3 text-sm">Para Empresas</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/subscriptions" className="hover:text-white transition-colors">Suscripciones</Link></li>
                <li><Link to="/login" className="hover:text-white transition-colors">Dashboard</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-3 text-sm">Para Clientes</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/menu" className="hover:text-white transition-colors">Ver Menú</Link></li>
                <li><Link to="/login" className="hover:text-white transition-colors">Mi Cuenta</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-3 text-sm">Contacto</h4>
              <ul className="space-y-2 text-sm">
                <li>hola@lunchroll.mx</li>
                <li>CDMX, México</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-stone-800 pt-6 text-center">
            <p className="text-sm">© 2026 Lunch & Roll. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
